import {Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {NgForm} from '@angular/forms';
import {RegisterCompanyStepBase} from '../register-company-step.base';
import {
    AddressLookupResult,
    AddressLookupService
} from '../../../../services/address-lookup/address-lookup.service';

interface PhoneCountry {
    iso: string;
    name: string;
    flag: string;
    dialCode: string;
}

@Component({
    selector: 'app-register-company-legal-contacts',
    templateUrl: './legal-contacts-step.component.html',
    styleUrls: ['../../register-company.component.scss']
})
export class RegisterCompanyLegalContactsStepComponent extends RegisterCompanyStepBase implements OnInit, OnDestroy {

    @Input() provinces: { id: string; name: string }[] = [];
    @Input() cities: any[] = [];
    @Input() showPassword = false;

    @Output() showPasswordChange = new EventEmitter<boolean>();
    @Output() legalCitiesNeedUpdate = new EventEmitter<void>();

    readonly phoneCountries: PhoneCountry[] = [
        {iso: 'IT', name: 'Italia', flag: '🇮🇹', dialCode: '+39'},
        {iso: 'CH', name: 'Svizzera', flag: '🇨🇭', dialCode: '+41'},
        {iso: 'FR', name: 'Francia', flag: '🇫🇷', dialCode: '+33'},
        {iso: 'DE', name: 'Germania', flag: '🇩🇪', dialCode: '+49'},
        {iso: 'ES', name: 'Spagna', flag: '🇪🇸', dialCode: '+34'},
        {iso: 'AT', name: 'Austria', flag: '🇦🇹', dialCode: '+43'},
        {iso: 'BE', name: 'Belgio', flag: '🇧🇪', dialCode: '+32'},
        {iso: 'NL', name: 'Paesi Bassi', flag: '🇳🇱', dialCode: '+31'},
        {iso: 'PT', name: 'Portogallo', flag: '🇵🇹', dialCode: '+351'},
        {iso: 'GB', name: 'Regno Unito', flag: '🇬🇧', dialCode: '+44'},
        {iso: 'US', name: 'Stati Uniti', flag: '🇺🇸', dialCode: '+1'},
    ];

    addressResults: AddressLookupResult[] = [];
    addressSearchError = '';
    isSearchingAddress = false;
    isAddressDropdownOpen = false;
    highlightedAddressIndex = 0;
    isPhoneCountryOpen = false;
    submitted = false;

    private selectedPhoneDialCode = '+39';
    private searchDebounce: ReturnType<typeof setTimeout> | null = null;
    private searchRequestId = 0;
    private skipNextAddressSearch = false;
    private cityApplyTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor(private addressLookupService: AddressLookupService) {
        super();
    }

    ngOnInit(): void {
        if (this.company.legalProvince) {
            this.updateCitiesLegal();
        }
    }

    togglePasswordVisibility(): void {
        this.showPassword = !this.showPassword;
        this.showPasswordChange.emit(this.showPassword);
    }

    updateCitiesLegal(): void {
        this.legalCitiesNeedUpdate.emit();
    }

    onAddressChange(value: string): void {
        this.company.legalAddress = value;
        this.persistForm();
        this.addressSearchError = '';

        if (this.skipNextAddressSearch) {
            this.skipNextAddressSearch = false;
            return;
        }

        this.clearSearchDebounce();
        this.searchRequestId++;
        const query = (value ?? '').trim();
        if (query.length < 5) {
            this.addressResults = [];
            this.isAddressDropdownOpen = false;
            this.isSearchingAddress = false;
            return;
        }

        this.isSearchingAddress = true;
        this.isAddressDropdownOpen = true;
        this.searchDebounce = setTimeout(() => {
            void this.searchAddress();
        }, 350);
    }

    onAddressFocus(): void {
        if (this.addressResults.length > 0 || this.addressSearchError) {
            this.isAddressDropdownOpen = true;
        }
    }

    onAddressKeydown(event: KeyboardEvent): void {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (!this.isAddressDropdownOpen || this.addressResults.length === 0) {
                void this.searchAddress();
                return;
            }
            this.highlightedAddressIndex = Math.min(this.highlightedAddressIndex + 1, this.addressResults.length - 1);
            return;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.highlightedAddressIndex = Math.max(this.highlightedAddressIndex - 1, 0);
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            const selected = this.addressResults[this.highlightedAddressIndex];
            if (selected) {
                this.selectAddress(selected);
            } else {
                void this.searchAddress();
            }
            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            this.closeAddressDropdown();
        }
    }

    async searchAddress(): Promise<void> {
        const query = (this.company.legalAddress ?? '').trim();
        this.addressSearchError = '';
        this.clearSearchDebounce();

        if (query.length < 5) {
            this.addressResults = [];
            this.isAddressDropdownOpen = false;
            this.isSearchingAddress = false;
            return;
        }

        const requestId = ++this.searchRequestId;
        this.isSearchingAddress = true;
        this.isAddressDropdownOpen = true;

        try {
            const results = await this.addressLookupService.searchItalianAddress(query);
            if (requestId !== this.searchRequestId) return;

            this.addressResults = results;
            this.highlightedAddressIndex = 0;
            this.isAddressDropdownOpen = true;
            if (results.length === 0) {
                this.addressSearchError = 'Nessun indirizzo trovato. Prova ad aggiungere città o CAP.';
            }
        } catch (error) {
            if (requestId !== this.searchRequestId) return;
            console.error('Address lookup failed:', error);
            this.addressResults = [];
            this.addressSearchError = 'La ricerca indirizzo non è disponibile. Puoi compilare i campi manualmente.';
        } finally {
            if (requestId === this.searchRequestId) {
                this.isSearchingAddress = false;
            }
        }
    }

    selectAddress(result: AddressLookupResult): void {
        const address = result.address ?? {};
        const street = address.road ?? address.pedestrian ?? address.footway;
        const city = address.city ?? address.town ?? address.village ?? address.municipality;

        this.skipNextAddressSearch = true;
        this.company.legalAddress = street || result.display_name.split(',')[0];
        if (address.house_number) this.company.legalAddressNumber = address.house_number as any;
        if (address.postcode) this.company.legalZip = address.postcode;

        const provinceCode = this.resolveProvinceCode(address);
        if (provinceCode) {
            this.company.legalProvince = provinceCode;
            this.updateCitiesLegal();
        }
        this.scheduleCityApply(city);

        this.addressResults = [];
        this.addressSearchError = '';
        this.closeAddressDropdown();
        this.persistForm();
    }

    getAddressTitle(result: AddressLookupResult): string {
        const address = result.address ?? {};
        const street = [address.road ?? address.pedestrian ?? address.footway, address.house_number]
            .filter(Boolean)
            .join(' ');
        return street || result.display_name.split(',')[0];
    }

    getAddressSubtitle(result: AddressLookupResult): string {
        const address = result.address ?? {};
        const city = address.city ?? address.town ?? address.village ?? address.municipality;
        return [city, address.postcode].filter(Boolean).join(' · ');
    }

    get selectedPhoneCountry(): PhoneCountry {
        return this.phoneCountries.find(country => country.dialCode === this.phoneCountryDialCode)
            ?? this.phoneCountries[0];
    }

    togglePhoneCountryDropdown(): void {
        this.isPhoneCountryOpen = !this.isPhoneCountryOpen;
        if (this.isPhoneCountryOpen) {
            this.closeAddressDropdown();
        }
    }

    selectPhoneCountry(country: PhoneCountry): void {
        this.phoneCountryDialCode = country.dialCode;
        this.isPhoneCountryOpen = false;
    }

    get phoneCountryDialCode(): string {
        return this.detectPhoneCountry()?.dialCode ?? this.selectedPhoneDialCode;
    }

    set phoneCountryDialCode(dialCode: string) {
        const nationalNumber = this.phoneNationalNumber;
        this.selectedPhoneDialCode = dialCode;
        this.company.legalPhone = nationalNumber ? `${dialCode}${nationalNumber}` : '';
        this.persistForm();
    }

    get phoneNationalNumber(): string {
        const rawPhone = this.company.legalPhone?.trim() ?? '';
        const country = this.detectPhoneCountry();
        const withoutPrefix = country && rawPhone.startsWith(country.dialCode)
            ? rawPhone.slice(country.dialCode.length)
            : rawPhone;
        return withoutPrefix.replace(/\D/g, '');
    }

    set phoneNationalNumber(value: string) {
        const digits = (value ?? '').replace(/\D/g, '').slice(0, 14);
        this.company.legalPhone = digits ? `${this.phoneCountryDialCode}${digits}` : '';
        this.persistForm();
    }

    get formattedPhoneNationalNumber(): string {
        return this.formatNationalNumber(this.phoneNationalNumber);
    }

    onPhoneInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const digitsBeforeCursor = input.value
            .slice(0, input.selectionStart ?? input.value.length)
            .replace(/\D/g, '').length;
        this.phoneNationalNumber = input.value;
        const formatted = this.formattedPhoneNationalNumber;
        input.value = formatted;
        const position = this.cursorFromDigitIndex(formatted, digitsBeforeCursor);
        input.setSelectionRange(position, position);
    }

    get isPhoneValid(): boolean {
        const digitCount = (this.company.legalPhone ?? '').replace(/\D/g, '').length;
        return digitCount >= 7 && digitCount <= 15;
    }

    private formatNationalNumber(digits: string): string {
        if (!digits) return '';

        if (this.selectedPhoneCountry.iso === 'IT') {
            if (digits.startsWith('3')) {
                return [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 10)]
                    .filter(Boolean)
                    .join(' ');
            }
            const areaLength = /^0(2|6)/.test(digits) ? 2 : Math.min(3, digits.length);
            const rest = digits.slice(areaLength).replace(/(\d{3})(?=\d)/g, '$1 ').trim();
            return [digits.slice(0, areaLength), rest].filter(Boolean).join(' ');
        }

        return digits.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
    }

    private cursorFromDigitIndex(formatted: string, digitIndex: number): number {
        if (digitIndex <= 0) return 0;
        let seen = 0;
        for (let i = 0; i < formatted.length; i++) {
            if (/\d/.test(formatted[i])) {
                seen++;
                if (seen === digitIndex) return i + 1;
            }
        }
        return formatted.length;
    }

    tryContinue(form: NgForm): void {
        this.submitted = true;
        form.control.markAllAsTouched();
        if (form.invalid || !this.isPhoneValid) return;
        this.nextStep();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement | null;
        if (!target?.closest('[data-address-autocomplete]')) {
            this.closeAddressDropdown();
        }
        if (!target?.closest('[data-phone-country]')) {
            this.isPhoneCountryOpen = false;
        }
    }

    ngOnDestroy(): void {
        this.clearSearchDebounce();
        if (this.cityApplyTimeout) {
            clearTimeout(this.cityApplyTimeout);
            this.cityApplyTimeout = null;
        }
    }

    private closeAddressDropdown(): void {
        this.isAddressDropdownOpen = false;
        this.highlightedAddressIndex = 0;
    }

    private clearSearchDebounce(): void {
        if (this.searchDebounce) {
            clearTimeout(this.searchDebounce);
            this.searchDebounce = null;
        }
    }

    private scheduleCityApply(city?: string): void {
        if (!city) return;
        if (this.cityApplyTimeout) clearTimeout(this.cityApplyTimeout);
        this.cityApplyTimeout = setTimeout(() => {
            this.company.legalCity = this.matchCity(city);
            this.persistForm();
            this.cityApplyTimeout = null;
        });
    }

    private matchCity(city: string): string {
        const normalized = this.normalizeProvinceName(city);
        return this.cities.find(option => this.normalizeProvinceName(String(option)) === normalized) ?? city;
    }

    private detectPhoneCountry(): PhoneCountry | undefined {
        const phone = this.company.legalPhone?.trim() ?? '';
        return [...this.phoneCountries]
            .sort((a, b) => b.dialCode.length - a.dialCode.length)
            .find(country => phone.startsWith(country.dialCode));
    }

    private resolveProvinceCode(address: Record<string, string | undefined>): string | undefined {
        const isoProvince = Object.entries(address)
            .find(([key, value]) => key.startsWith('ISO3166-2-lvl') && /^IT-[A-Z]{2}$/i.test(value ?? ''))?.[1]
            ?.split('-')[1]
            ?.toUpperCase();
        if (isoProvince && this.provinces.some(province => province.id === isoProvince)) return isoProvince;

        const candidates = [address['county'], address['state_district']]
            .filter((value): value is string => !!value)
            .map(value => this.normalizeProvinceName(value));
        return this.provinces.find(province => candidates.includes(this.normalizeProvinceName(province.name)))?.id;
    }

    private normalizeProvinceName(value: string): string {
        return value
            .toLocaleLowerCase('it')
            .replace(/provincia (di|del|della) /g, '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim();
    }

    isPasswordValid(): boolean {
        if (!this.company.legalPassword) return false;
        const hasUpper = /[A-Z]/.test(this.company.legalPassword);
        const hasLower = /[a-z]/.test(this.company.legalPassword);
        const hasNumber = /[0-9]/.test(this.company.legalPassword);
        const hasSpecial = /[\W_]/.test(this.company.legalPassword);
        const hasMinLength = this.company.legalPassword.length >= 8;
        return hasUpper && hasLower && hasNumber && hasSpecial && hasMinLength;
    }
}
